import type { ComponentProps } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Spinner } from "@volca/ui/components/ui/spinner";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@volca/ui/components/ui/toggle-group";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { cn } from "@volca/ui/lib/utils";
import { IcmPeriodPicker } from "@/icm/common/icm-period-picker";
import { ARTIFACT_CONTROLS } from "@/payroll/editor/constants";
import { proposalError } from "@/payroll/editor/state";
import type { ComparisonState } from "@/payroll/editor/types";
import { LENS, LENS_LABEL, type Lens } from "./lens";

const CONTROL = "h-7 rounded-md px-2.5 text-xs font-medium";

/**
 * The period to compare over, the run control, and which reading of the run to
 * show. Everything that decides WHAT is on screen, before the screen itself.
 */
export const ComparisonToolbar = ({
  data,
  state,
  run,
  periodStart,
  setPeriodStart,
  pickerPeriods,
  payMayMove,
  editable,
  lens,
  setLens,
}: {
  data: ComparisonState | undefined;
  state: { isPending: boolean; isFetching: boolean };
  run: {
    isPending: boolean;
    isError: boolean;
    error: unknown;
    mutate: () => void;
  };
  periodStart: string | undefined;
  setPeriodStart: (value: string) => void;
  pickerPeriods: ComponentProps<typeof IcmPeriodPicker>["periods"];
  payMayMove: boolean;
  editable: boolean;
  lens: Lens;
  setLens: (lens: Lens) => void;
}) => (
  // `border-b-0`: on Pay this row is the top of a three-band grey block, not
  // the edge of it. The block's wrapper draws the one strong line at its foot.
  <div
    className={cn(
      ARTIFACT_CONTROLS,
      "flex flex-wrap items-center gap-1.5 border-b-0",
    )}
  >
    <IcmPeriodPicker
      size="sm"
      className="w-52"
      periods={pickerPeriods}
      calendarPeriods={pickerPeriods}
      selectedPeriodStart={periodStart ?? data?.period.start_date}
      isLoading={state.isFetching}
      disabled={data?.running}
      onSelect={setPeriodStart}
    />
    {/* A closed proposal has nothing left to compare against: its draft is
          no longer the one a run would use, and the rest of the workspace is
          already read-only. Offering a five-minute run here is an invitation
          to waste it. */}
    {!!editable && (
      <Tooltip disabled={payMayMove}>
        <TooltipTrigger render={<span className="inline-flex" />}>
          <Button
            size="xs"
            className={CONTROL}
            disabled={run.isPending || data?.running || !payMayMove}
            aria-busy={run.isPending || data?.running || undefined}
            onClick={() => run.mutate()}
          >
            {(run.isPending || data?.running) && (
              <Spinner aria-hidden data-icon="inline-start" />
            )}
            {data?.ready ? "Run again" : "Run comparison"}
          </Button>
        </TooltipTrigger>
        <TooltipContent>There is no change that could move pay</TooltipContent>
      </Tooltip>
    )}
    {!!data?.running && (
      <p className="text-xs text-subtle" role="status">
        Running both versions over this period. This takes a few minutes.
      </p>
    )}
    {!!run.isError && (
      <p className="text-xs" role="alert">
        {proposalError(run.error)}
      </p>
    )}
    {!!data?.error && !data.running && (
      <p className="text-xs" role="alert">
        {data.error}
      </p>
    )}
    {/* Last, and pushed to the far edge.

        It sat third, between Run again and the run's own status line, which put
        a view switch in the middle of the controls that produce the thing being
        viewed. The period and the run button are inputs: they decide what gets
        calculated. This decides how you look at the answer, so it reads as the
        other end of the row, and the status line stays next to the button that
        causes it. */}
    <ToggleGroup
      variant="outline"
      size="sm"
      spacing={0}
      aria-label="Comparison view"
      value={[lens]}
      onValueChange={(next) => {
        const value = Object.values(LENS).find((option) => option === next[0]);
        if (value) setLens(value);
      }}
      className="ml-auto"
    >
      {Object.values(LENS).map((option) => (
        <ToggleGroupItem key={option} value={option} className="text-xs">
          {LENS_LABEL[option]}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  </div>
);
